/* eslint-disable import/no-deprecated */
import { Q } from '.';
import {
  BehaviorSubject,
  NEVER,
  Observable,
  animationFrames,
  map,
  sample,
  switchAll,
} from 'rxjs';

// Base interface type for any class that has a View. Basically the "Component interface".
export interface HasView<V extends Q.OfOne> {
  view(): V;
}

// A Component is the 'Presenter' in an MVP architecture.
// It composites a View, has state (the Model), and handles logic and wiring up of disparate Views.
export class Component<V extends Q.OfOne> implements HasView<V> {
  private _view: V;

  constructor(view: V) {
    this._view = view;
  }

  view(): V {
    return this._view;
  }
}

/**
 * A component with a simple lifecycle transitioning from "detached"
 * to "attached" and back.
 *
 * A similar informal pattern used throughout Tomato code uses
 * show()/hide()/teardown() etc methods.
 *
 * A DetachableComponent (or its owner) is responsible for updating
 * its own lifecycle by calling attach() and detach().  An alternative
 * approach could use DOM APIs like IntersectionObserver to track
 * element visibility automatically; though note there's no simple way
 * to subscribe a single node being added / removed from the DOM,
 * unless the node is a Custom Element.
 */
export abstract class DetachableComponent<V extends Q.OfOne> extends Component<V> {
  private _attached$ = new BehaviorSubject(false);
  protected get attached$(): Observable<boolean> {
    return this._attached$;
  }
  /**
   * Notify this component that it's potentially visible and to
   * initialize render loops, subscribe to events, etc.
   */
  attach() {
    this._attached$.next(true);
  }
  /**
   * Tell this component it's hidden, and to end render loops, unsubscribe
   * from events, etc.
   */
  detach() {
    this._attached$.next(false);
  }

  sampleToRender<T>($state: Observable<T>): Observable<T> {
    return this.attached$.pipe(
      map(x => x ? $state : NEVER),
      switchAll(),
      sample(animationFrames()),
    );
  }
}
